import type { FC } from "react";

import styled from "styled-components";

import { DEFAULT_POSITION_SORTING } from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import type { KeywordsReportTableFilter } from "app/components/campaigns/shared/CampaignsConst";
import { getLocaleForScrapingConfig } from "app/components/campaigns/shared/CampaignsUtils";
import { ERankTrackerPerformanceOvertimeTabs } from "app/components/campaigns/shared/overview/types";
import { sanitizeUseCaseFilter } from "app/components/campaigns/shared/utils";
import { DASHBOARD_ID_PREVIEW_MODE } from "app/components/dashboard/widget-wizard/constants";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { TableTemplateV2 } from "app/components/widget/dashboard/TableTemplate-v2";
import type { IDashboardWidgetInterface } from "app/components/widget/utils/DashboardWidgetsInterfaces-v2";
import { getDurationForURLWithCampaignMax } from "app/components/widget/widget-utilities/Campaigns/CampaignsUtils";
import { WidgetLegendContainerV2 } from "app/dashboards/view-components/widget-legend/WidgetLegendContainer-v2";
import type { IRankTrackerKeywordsReportTableWidget } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { EKeywordsReportKeywordsTableMetrics } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { KeywordsReportKeywordsTablePresets } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/tablePresets/KeywordsReportKeywordsTablePresets";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const LegendsWrapper = styled.div`
    margin-top: 8px;
`;
const PresetsWrapper = styled.div`
    margin-top: 20px;
    margin-bottom: 16px;
`;

const TableWrraper = styled.div`
    .swReactTableCell {
        height: 40px;
        align-items: center;
    }
    .swTable--simple {
        .swTable-cell {
            padding: 10px 12px;
        }
    }
`;

export const RankTrackerKeywordsTableTemplate: FC<IDashboardWidgetInterface<IRankTrackerKeywordsReportTableWidget>> = (
    props,
) => {
    const { widget } = props;

    const currentCampaign = widget._widgetConfig.properties.currentCampaignConfiguration;
    const legeneds: ILegendItems[] = widget._params.keys?.split(",").map((key) => {
        return {
            name: key,
            visible: true,
        };
    });

    const sanitizedUseCaseFilter = sanitizeUseCaseFilter(
        widget._widgetConfig.properties.filters.useCaseFilter,
        currentCampaign?.positionExcludedSerpFeatures,
    );

    const currentStateParamsWithDevice = {
        ...widget._widgetConfig.properties.filters,
        useCaseFilter: sanitizedUseCaseFilter,
        duration: getDurationForURLWithCampaignMax(widget._widgetConfig.properties.duration, currentCampaign),
        device: widget._widgetConfig.properties.webSource,
        campaignId: widget._widgetConfig.properties.campaignguid,
        locationsIds: widget._widgetConfig.properties.country
            .split(",")
            .map((scrapingConfig) => {
                const config = currentCampaign.scrapingConfigurations.find((config) => config.id === scrapingConfig);
                return getLocaleForScrapingConfig(config);
            })
            .join("&"),
    };

    const handleCardSelected = (id: KeywordsReportTableFilter) => {
        if (widget._widgetConfig.dashboardId !== DASHBOARD_ID_PREVIEW_MODE) {
            const url = widget._metricConfig.state;
            const graphDefaultConfig = {
                graphGranularity: GranularityNames.DAILY,
                graphTab: ERankTrackerPerformanceOvertimeTabs.VISIBILITY,
            };
            const params = {
                ...graphDefaultConfig,
                ...currentStateParamsWithDevice,
                useCaseFilter: id,
                sites: widget._widgetConfig.properties.filters.selectedSites,
                sortedColumn: currentStateParamsWithDevice.sortedColumn ?? DEFAULT_POSITION_SORTING,
                tableTab: currentStateParamsWithDevice.tableTab ?? EKeywordsReportKeywordsTableMetrics.POSITION,
            };
            proNavigator.go(url, params);
        }
    };
    return (
        <>
            {legeneds.length > 1 ? (
                <LegendsWrapper>
                    <WidgetLegendContainerV2
                        widget={widget}
                        className="swWidget-legend audience-geography-table-legend"
                        items={legeneds}
                        useNewLegends={true}
                        useBulletLegends={true}
                    />
                </LegendsWrapper>
            ) : null}
            <PresetsWrapper>
                <KeywordsReportKeywordsTablePresets
                    currentCampaignConfiguration={currentCampaign}
                    stateParams={currentStateParamsWithDevice}
                    clickablePresets={false}
                    handleCardSelected={handleCardSelected}
                />
            </PresetsWrapper>
            <TableWrraper>
                <TableTemplateV2 {...props} />
            </TableWrraper>
        </>
    );
};
